"use client";

import {
  Children,
  Fragment,
  isValidElement,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  type ChangeEvent,
  type ReactElement,
  type ReactNode,
} from "react";
import { createPortal } from "react-dom";
import { Check, ChevronDown } from "lucide-react";
import { cn } from "@/lib/utils";

type Opt = { value: string; label: ReactNode; disabled?: boolean; group?: string };

/** Flatten <option> children (including fragments / mapped arrays) into a plain list. */
function readOptions(children: ReactNode, group?: string): Opt[] {
  const out: Opt[] = [];
  Children.forEach(children, (child) => {
    if (!isValidElement(child)) return;
    const el = child as ReactElement<{ value?: string | number; disabled?: boolean; children?: ReactNode; label?: string }>;
    if (el.type === Fragment) {
      out.push(...readOptions(el.props.children, group));
      return;
    }
    if (el.type === "optgroup") {
      out.push(...readOptions(el.props.children, el.props.label));
      return;
    }
    const label = el.props.children;
    const value = el.props.value !== undefined ? String(el.props.value) : typeof label === "string" ? label : "";
    out.push({ value, label, disabled: el.props.disabled, group });
  });
  return out;
}

/**
 * Drop-in replacement for a native <select> that renders the site's branded listbox
 * (cream panel, brand-tinted selection, brand scrollbar). Same props as <select>:
 * `value`, `onChange(e)` with `e.target.value`, and <option> children.
 */
export function Select({
  value,
  onChange,
  children,
  className,
  id,
  name,
  disabled,
  required,
  "aria-label": ariaLabel,
  "aria-invalid": ariaInvalid,
}: {
  value?: string | number;
  onChange?: (e: ChangeEvent<HTMLSelectElement>) => void;
  children: ReactNode;
  className?: string;
  id?: string;
  name?: string;
  disabled?: boolean;
  required?: boolean;
  "aria-label"?: string;
  "aria-invalid"?: boolean | "true" | "false";
}) {
  const [open, setOpen] = useState(false);
  const [rect, setRect] = useState<{ left: number; top: number; width: number; up: boolean } | null>(null);
  const btn = useRef<HTMLButtonElement>(null);
  const panel = useRef<HTMLDivElement>(null);

  const options = readOptions(children);
  const current = String(value ?? "");
  const selected = options.find((o) => o.value === current);
  const shown = selected ?? options[0];

  useLayoutEffect(() => {
    if (!open || !btn.current) return;
    const r = btn.current.getBoundingClientRect();
    const room = window.innerHeight - r.bottom;
    const up = room < 260 && r.top > room;
    setRect({ left: r.left, top: up ? r.top : r.bottom, width: r.width, up });
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const close = () => setOpen(false);
    const onDown = (e: MouseEvent) => {
      const t = e.target as Node;
      if (btn.current?.contains(t) || panel.current?.contains(t)) return;
      setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    const onScroll = (e: Event) => {
      if (panel.current?.contains(e.target as Node)) return;
      setOpen(false);
    };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    window.addEventListener("resize", close);
    window.addEventListener("scroll", onScroll, true);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
      window.removeEventListener("resize", close);
      window.removeEventListener("scroll", onScroll, true);
    };
  }, [open]);

  function pick(v: string) {
    setOpen(false);
    onChange?.({ target: { value: v, name }, currentTarget: { value: v, name } } as unknown as ChangeEvent<HTMLSelectElement>);
    btn.current?.focus();
  }

  return (
    <>
      <button
        ref={btn}
        type="button"
        id={id}
        disabled={disabled}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label={ariaLabel}
        aria-invalid={ariaInvalid}
        aria-required={required}
        onClick={() => setOpen((o) => !o)}
        className={cn(
          "inline-flex items-center justify-between gap-2 text-left disabled:cursor-not-allowed disabled:opacity-50",
          open && "!border-brand-600 !ring-4 !ring-brand-600/10",
          className
        )}
      >
        <span className={cn("truncate", !current && "text-ink-muted")}>{shown?.label}</span>
        <ChevronDown className={cn("h-4 w-4 shrink-0 text-ink-muted transition-transform", open && "rotate-180")} />
      </button>

      {open &&
        rect &&
        createPortal(
          <div
            ref={panel}
            role="listbox"
            style={{
              position: "fixed",
              left: Math.max(8, Math.min(rect.left, window.innerWidth - Math.min(Math.max(rect.width, 220), window.innerWidth - 16) - 8)),
              width: Math.min(Math.max(rect.width, 220), window.innerWidth - 16),
              ...(rect.up ? { bottom: window.innerHeight - rect.top + 4 } : { top: rect.top + 4 }),
            }}
            className="z-[1000] max-h-64 overflow-y-auto rounded-xl border border-line bg-cream p-1.5 text-sm shadow-lift [scrollbar-color:var(--color-brand-400)_var(--color-cream)] [scrollbar-width:thin]"
          >
            {options.map((o, i) => {
              const on = o.value === current;
              const heading = o.group && o.group !== options[i - 1]?.group;
              return (
                <Fragment key={i}>
                {heading && <div className="px-3 pb-1 pt-2 text-[11px] font-bold uppercase tracking-wider text-ink-muted">{o.group}</div>}
                <button
                  type="button"
                  role="option"
                  aria-selected={on}
                  disabled={o.disabled}
                  onClick={() => pick(o.value)}
                  className={cn(
                    "flex w-full items-center justify-between gap-2 rounded-lg px-3 py-2 text-left text-sm font-normal normal-case tracking-normal transition-colors hover:bg-brand-600/8 disabled:opacity-40",
                    on ? "bg-brand-600/10 font-semibold text-brand-700" : "text-ink"
                  )}
                >
                  <span className="min-w-0 break-words">{o.label}</span>
                  {on && <Check className="h-4 w-4 shrink-0" strokeWidth={2.5} />}
                </button>
                </Fragment>
              );
            })}
          </div>,
          document.body
        )}
    </>
  );
}
